import { useEffect, useRef } from 'react'

interface Point {
  x: number
  y: number
}

/** One "share price" run: points across the page, always ending higher. */
interface Chart {
  points: Array<Point>
  /** Indexes of local highs, marked with a ring once the line passes them. */
  highs: Array<number>
  age: number
}

interface Palette {
  dot: string
  chart: string
}

const PITCH = 14
const MAX_DOT_RADIUS = PITCH * 0.17
const CHART_STEP = 12
/** Depth under the line over which the area's dots shrink to their base size. */
const AREA_DEPTH = 150
const POINTER_RADIUS = 90
/** Dots smaller than this are not drawn — and the pointer never wakes them. */
const MIN_VISIBLE_RADIUS = 0.35
const DRAW_SECONDS = 16
const HOLD_SECONDS = 6
const FADE_SECONDS = 2
const REST_SECONDS = 1.5
const MAX_DEVICE_PIXEL_RATIO = 2

function createSeededRandom(seed: number) {
  let value = seed >>> 0

  return () => {
    value = (value * 1664525 + 1013904223) >>> 0
    return value / 4294967296
  }
}

function smoothstep(edge0: number, edge1: number, value: number) {
  const t = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

/** Slow, soft tonal field in 0..1 — the "engraving" under the dots. */
function tone(x: number, y: number, time: number) {
  const a = Math.sin(x * 0.011 + time * 0.15) * Math.cos(y * 0.013 - time * 0.1)
  const b = Math.sin((x + y) * 0.006 + time * 0.07)
  return 0.5 + a * 0.3 + b * 0.2
}

/**
 * A stock-like random walk across the page. Every run gets its own start,
 * finish and curve, and a volatile path with sudden "news" jumps — but the
 * finish is always well above the start.
 */
function createChart(
  width: number,
  height: number,
  random: () => number,
): Chart {
  const count = Math.ceil((width + 40) / CHART_STEP) + 1
  // Start in the lower third, finish in the upper third: always up overall.
  const startY = height * (0.7 + random() * 0.22)
  const endY = height * (0.1 + random() * 0.25)
  // Below 1 the rally comes early and flattens; above 1 it accelerates late.
  const curve = 0.7 + random() * 1.1
  const swing = height * (0.12 + random() * 0.08)

  // Random walk with momentum and occasional shocks, then detrended so it
  // starts and ends at 0.
  const walk = [0]
  let velocity = 0
  for (let index = 1; index < count; index += 1) {
    velocity = velocity * 0.7 + (random() - 0.5) * 1.4
    if (random() < 0.06) velocity += (random() - 0.5) * 6
    walk.push(walk[index - 1] + velocity)
  }
  const last = walk[count - 1]
  const detrended = walk.map(
    (value, index) => value - (last * index) / (count - 1),
  )
  const peak = Math.max(1e-6, ...detrended.map(Math.abs))

  const points = detrended.map((value, index) => {
    const progress = index / (count - 1)
    const trend = startY + (endY - startY) * Math.pow(progress, curve)
    // Ends stay close to the trend; the middle swings hardest.
    const envelope = 0.25 + 0.75 * Math.pow(Math.sin(progress * Math.PI), 0.6)
    let y = trend + (value / peak) * swing * envelope
    // Pullbacks yes, but the price never sinks much below where it opened: a
    // dip past that floor bounces back up, so the line stays jagged, not flat.
    const floor = startY + height * 0.025
    if (y > floor) y = floor - (y - floor) * 0.6
    return {
      x: -20 + index * CHART_STEP,
      y: Math.max(height * 0.06, y),
    }
  })

  // Highs are judged against the trend (on the detrended walk): a lower value
  // sits higher on screen, so a local minimum is a local peak in price.
  const highs: Array<number> = []
  for (let index = 3; index < count - 3; index += 1) {
    const value = detrended[index]
    const isHigh = [-3, -2, -1, 1, 2, 3].every(
      (offset) => detrended[index + offset] >= value,
    )
    if (
      isHigh &&
      (highs.length === 0 || index - highs[highs.length - 1] > 14)
    ) {
      highs.push(index)
    }
  }

  return { points, highs, age: 0 }
}

/**
 * Newsprint halftone backdrop for the GTM pages: a quiet dot screen along the
 * page edges (the pointer only magnifies dots that are there), with a green
 * share-price line slowly drawing itself upward across the page — the halftone under the line fills in like an area chart,
 * and each new high gets a ring. Colours come from `--halftone-dot` and
 * `--halftone-chart` (styles.css, `.theme-gtm`).
 */
export function GtmHalftoneBackground({ quiet = false }: { quiet?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')

    if (!canvas || !context) return

    const reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    )
    let prefersReducedMotion = reducedMotionQuery.matches
    const baseAlpha = quiet ? 0.07 : 0.11
    const chartAlpha = quiet ? 0.6 : 0.85
    const random = createSeededRandom(Date.now())
    let width = 0
    let height = 0
    let palette: Palette = { dot: '#121212', chart: '#1f7a45' }
    let elapsed = 0
    let previousTime = 0
    let animationFrameId = 0
    let chart: Chart | null = null
    let pointer: Point | null = null

    const readPalette = (): Palette => {
      const styles = window.getComputedStyle(canvas)
      return {
        dot: styles.getPropertyValue('--halftone-dot').trim() || palette.dot,
        chart:
          styles.getPropertyValue('--halftone-chart').trim() || palette.chart,
      }
    }

    const resizeCanvas = () => {
      width = window.innerWidth
      height = window.innerHeight
      const pixelRatio = Math.min(
        window.devicePixelRatio || 1,
        MAX_DEVICE_PIXEL_RATIO,
      )
      canvas.width = Math.round(width * pixelRatio)
      canvas.height = Math.round(height * pixelRatio)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
      palette = readPalette()
      chart = createChart(width, height, random)
      if (prefersReducedMotion) chart.age = DRAW_SECONDS
    }

    const distanceFromCentre = (x: number) =>
      Math.abs(x - width / 2) / (width / 2)

    /** The dot screen lives only along the page edges. */
    const edgeMask = (x: number) => smoothstep(0.6, 1, distanceFromCentre(x))

    /** The chart crosses the page but thins out under the content column. */
    const columnMask = (x: number) =>
      0.12 + 0.88 * smoothstep(0.2, 0.95, distanceFromCentre(x))

    /** How far the line has drawn (0..1) and how visible the chart is (0..1). */
    const chartState = () => {
      if (!chart) return { drawn: 0, visible: 0 }
      // Constant speed: easing made the line rush, then crawl.
      const drawn = Math.min(1, chart.age / DRAW_SECONDS)
      const fadeStart = DRAW_SECONDS + HOLD_SECONDS
      const visible = prefersReducedMotion
        ? 1
        : 1 - smoothstep(fadeStart, fadeStart + FADE_SECONDS, chart.age)
      return { drawn, visible }
    }

    /** Line height at x, or null where the line has not been drawn yet. */
    const lineYAt = (x: number, headX: number) => {
      if (!chart || x > headX) return null
      const position = (x + 20) / CHART_STEP
      const index = Math.floor(position)
      const lastIndex = chart.points.length - 1
      if (index < 0 || index > lastIndex) return null
      const from = chart.points[index]
      const to = chart.points[Math.min(index + 1, lastIndex)]
      return from.y + (to.y - from.y) * (position - index)
    }

    const pointerBoost = (x: number, y: number) => {
      if (!pointer) return 0
      const distance = Math.hypot(pointer.x - x, pointer.y - y)
      return distance > POINTER_RADIUS
        ? 0
        : smoothstep(0, 1, 1 - distance / POINTER_RADIUS) * 0.55
    }

    const step = (deltaSeconds: number) => {
      elapsed += deltaSeconds
      if (prefersReducedMotion || !chart) return
      chart.age += deltaSeconds
      if (
        chart.age >
        DRAW_SECONDS + HOLD_SECONDS + FADE_SECONDS + REST_SECONDS
      ) {
        chart = createChart(width, height, random)
      }
    }

    const draw = () => {
      context.clearRect(0, 0, width, height)
      const time = prefersReducedMotion ? 0 : elapsed
      const { drawn, visible } = chartState()
      const points = chart?.points ?? []
      // The head moves continuously inside a segment, not point to point.
      const lastIndex = Math.max(0, points.length - 1)
      const headPosition = drawn * lastIndex
      const headIndex = Math.min(lastIndex, Math.floor(headPosition))
      const headFrom = points.length ? points[headIndex] : null
      const headTo = points.length
        ? points[Math.min(headIndex + 1, lastIndex)]
        : null
      const fraction = headPosition - headIndex
      const head =
        headFrom && headTo
          ? {
              x: headFrom.x + (headTo.x - headFrom.x) * fraction,
              y: headFrom.y + (headTo.y - headFrom.y) * fraction,
            }
          : null
      const headX = head ? head.x : -Infinity
      const swollen: Array<[number, number, number]> = []
      const filled: Array<[number, number, number]> = []

      context.fillStyle = palette.dot
      context.globalAlpha = baseAlpha
      context.beginPath()

      for (let y = PITCH / 2; y < height; y += PITCH) {
        // Every other row is offset half a pitch, as in a printed screen.
        const rowOffset = (Math.round(y / PITCH) % 2) * (PITCH / 2)
        for (let x = PITCH / 2 + rowOffset; x < width; x += PITCH) {
          const mask = columnMask(x)
          const radius =
            MAX_DOT_RADIUS * edgeMask(x) * (0.25 + 0.75 * tone(x, y, time))

          // Halftone area under the drawn line, all the way to the bottom:
          // larger dots right under the line, an even screen below that.
          const lineY = visible > 0 ? lineYAt(x, headX) : null
          if (lineY !== null && y > lineY) {
            const depth = Math.max(0, 1 - (y - lineY) / AREA_DEPTH)
            // Dots at the advancing front grow in instead of popping up a
            // whole column at a time.
            const grow = smoothstep(0, PITCH * 3, headX - x)
            filled.push([
              x,
              y,
              MAX_DOT_RADIUS *
                (0.8 + 0.45 * depth) *
                (0.45 + 0.55 * mask) *
                grow,
            ])
            continue
          }

          if (radius < MIN_VISIBLE_RADIUS) continue

          // Only dots already on the page react to the pointer: moving over
          // blank paper or the chart's area shows nothing new.
          const boost = pointerBoost(x, y)
          if (boost > 0.08) {
            swollen.push([x, y, radius + boost * PITCH * 0.22])
            continue
          }
          context.moveTo(x + radius, y)
          context.arc(x, y, radius, 0, Math.PI * 2)
        }
      }
      context.fill()

      if (swollen.length > 0) {
        context.globalAlpha = quiet ? 0.24 : 0.36
        context.beginPath()
        for (const [x, y, radius] of swollen) {
          context.moveTo(x + radius, y)
          context.arc(x, y, radius, 0, Math.PI * 2)
        }
        context.fill()
      }

      if (!chart || visible <= 0) {
        context.globalAlpha = 1
        return
      }

      context.fillStyle = palette.chart
      context.globalAlpha = visible * (quiet ? 0.18 : 0.3)
      context.beginPath()
      for (const [x, y, radius] of filled) {
        context.moveTo(x + radius, y)
        context.arc(x, y, radius, 0, Math.PI * 2)
      }
      context.fill()

      // The price line, segment by segment so it fades under the copy.
      context.strokeStyle = palette.chart
      context.lineWidth = 1.6
      context.lineJoin = 'round'
      context.lineCap = 'round'
      for (let index = 1; index <= headIndex; index += 1) {
        const from = points[index - 1]
        const to = points[index]
        context.globalAlpha =
          visible * chartAlpha * (0.25 + 0.75 * columnMask(to.x))
        context.beginPath()
        context.moveTo(from.x, from.y)
        context.lineTo(to.x, to.y)
        context.stroke()
      }
      if (head && headFrom && fraction > 0) {
        context.globalAlpha =
          visible * chartAlpha * (0.25 + 0.75 * columnMask(head.x))
        context.beginPath()
        context.moveTo(headFrom.x, headFrom.y)
        context.lineTo(head.x, head.y)
        context.stroke()
      }

      // Rings on the highs already passed: the leads and meetings it found.
      context.lineWidth = 1.2
      for (const index of chart.highs) {
        if (index > headIndex) break
        const point = points[index]
        const mask = columnMask(point.x)
        if (mask < 0.5) continue
        context.globalAlpha = visible * chartAlpha * mask
        context.beginPath()
        context.arc(point.x, point.y, 4.5, 0, Math.PI * 2)
        context.stroke()
      }

      if (!head) {
        context.globalAlpha = 1
        return
      }

      // The live "last price" at the head, with a slow pulse.
      const pulse = prefersReducedMotion ? 0 : (elapsed * 0.8) % 1
      context.fillStyle = palette.chart
      context.globalAlpha = visible * chartAlpha
      context.beginPath()
      context.arc(head.x, head.y, 3.4, 0, Math.PI * 2)
      context.fill()
      if (!prefersReducedMotion) {
        context.globalAlpha = visible * chartAlpha * (1 - pulse) * 0.6
        context.lineWidth = 1
        context.beginPath()
        context.arc(head.x, head.y, 4 + pulse * 12, 0, Math.PI * 2)
        context.stroke()
      }

      context.globalAlpha = 1
    }

    const animate = (now: number) => {
      animationFrameId = window.requestAnimationFrame(animate)
      const deltaSeconds = previousTime
        ? Math.min((now - previousTime) / 1000, 0.1)
        : 0
      previousTime = now
      step(deltaSeconds)
      draw()
    }

    const start = () => {
      window.cancelAnimationFrame(animationFrameId)
      previousTime = 0
      if (prefersReducedMotion || document.hidden) {
        draw()
        return
      }
      animationFrameId = window.requestAnimationFrame(animate)
    }

    const handleResize = () => {
      resizeCanvas()
      draw()
    }
    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch' || prefersReducedMotion) return
      pointer = { x: event.clientX, y: event.clientY }
    }
    const handlePointerOut = (event: PointerEvent) => {
      if (event.relatedTarget === null) pointer = null
    }
    const handleMotionChange = (event: MediaQueryListEvent) => {
      prefersReducedMotion = event.matches
      pointer = null
      if (chart && prefersReducedMotion) chart.age = DRAW_SECONDS
      start()
    }
    const handleVisibilityChange = () => start()

    resizeCanvas()
    draw()
    start()

    window.addEventListener('resize', handleResize)
    window.addEventListener('pointermove', handlePointerMove, { passive: true })
    window.addEventListener('pointerout', handlePointerOut, { passive: true })
    document.addEventListener('visibilitychange', handleVisibilityChange)
    reducedMotionQuery.addEventListener('change', handleMotionChange)

    return () => {
      window.cancelAnimationFrame(animationFrameId)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerout', handlePointerOut)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      reducedMotionQuery.removeEventListener('change', handleMotionChange)
    }
  }, [quiet])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 h-full w-full"
    />
  )
}
